import React, { useEffect, useMemo, useState, Fragment } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { MdOutlineZoomOutMap, MdAccessTime, MdHistory, MdContentCopy } from "react-icons/md";
import { MdCheck } from "react-icons/md";
import { Button, Menu, MenuButton, MenuItems, MenuItem, Transition } from "@headlessui/react";
import { IoCloseSharp } from "react-icons/io5";
import { Edit2, Trash2, MoreVertical, Lock, Check, ChevronRight, RotateCcw } from "lucide-react";
import { FiLink, FiCheck } from "react-icons/fi";
import Breadcrumbs from "components/common/Breadcrumbs";
import Comments from "components/modules/ProjectManagement/Projects/Comments";
import ActivityTabs from "components/modules/ProjectManagement/Projects/ActivityTabs";
import WorkLog from "components/modules/ProjectManagement/Projects/WorkLog";
import TipTapEditor from "components/common/RichTextEditor/RichTextEditor";
import { WorkType, WorkTypeCategory, WorkStatus, WorkStatusCategory } from "types/project/projectDefaults";
import { ProjectUser } from "types/project";
import TaskTypeDropdown, { workTypeUIMap } from "components/modules/ProjectManagement/Dropdowns/TaskTypeDropdown";
import StatusDropdown from "components/modules/ProjectManagement/Dropdowns/StatusDropdown";
import UserDropdown from "components/modules/ProjectManagement/Dropdowns/UserDropdown";
import { PriorityDropdown, LabelPicker } from "components/modules/ProjectManagement/Dropdowns/index";
import TaskDetailSkeleton from "components/Loaders/TaskDetailSkeleton";
import CustomCalendar from "components/common/CustomCalendar";
import { formatDateKey, HIERARCHY_RULES, normalizeEmptyParagraphs } from "utils/helpers";
import { LoaderButton } from "components/common/LoadingButton";
import ChildItemsList from "components/modules/ProjectManagement/ChildItemsList";
import LinkedItemsList from "components/modules/ProjectManagement/LinkedItemsList";
import AttachmentList from "components/modules/ProjectManagement/AttachmentList";
import { CollapsibleSection } from "components/common/CollapsibleSection";
import TimeTrackerSidebar from "components/modules/ProjectManagement/Projects/TimeTrackerSidebar";
import StoryPointsInput from "components/modules/ProjectManagement/StoryPointsInput";
import SprintDestinationDropdown from "components/modules/ProjectManagement/Dropdowns/SprintDestinationDropdown";
import EpicPickerDropdown from "components/modules/ProjectManagement/Dropdowns/EpicPickerDropdown";
import ConfirmDeleteModal from "components/Modals/ConfirmDeleteModal";
import { usePmoNavigation } from "hooks/usePmoNavigation";
import { useAppSelector } from "store";
import { selectAuthUser } from "store/slices/auth/auth.selector";
import TaskHistory from "components/modules/ProjectManagement/Projects/TaskHistory";
import ImagePreviewModal from "components/common/ImagePreviewModal";
import EnhancedDeleteModal from "components/Modals/EnhancedDeleteModal";
import { useTaskForm, SavedWorkItem } from "hooks/useTaskForm";
import { toast } from "react-hot-toast";
import ChangeParentModal from "components/Modals/ChangeParentModal";
import ConvertWorkItemModal from "components/Modals/ConvertWorkItemModal";
import EpicProgress from "components/modules/ProjectManagement/Epic/EpicProgress";
import EpicStatusBanner from "components/modules/ProjectManagement/Epic/EpicStatusBanner";
import EpicChildItems from "components/modules/ProjectManagement/Epic/EpicChildItems";
import MarkEpicDoneModal from "components/modules/ProjectManagement/Epic/MarkEpicDoneModal";
import LinkEpicChildrenModal from "components/modules/ProjectManagement/Epic/LinkEpicChildrenModal";
import { useEpicQuery, useMarkEpicDoneMutation, useProjectEpicsQuery, useReopenEpicMutation } from "service/project/hooks/useEpicQueries";
import { EpicSummary } from "types/project/epic";
import { Task } from "types/project/tasks";
import { useEpicDonePrompt } from "hooks/useEpicDonePrompt";

const isDescriptionEmpty = (html: string | undefined | null) => {
  if (!html) return true;
  if (html.includes("<img") || html.includes("<iframe") || html.includes("<video")) {
    return false;
  }
  const cleanText = html.replace(/<[^>]*>/g, "").trim();
  const withoutEntities = cleanText.replace(/&nbsp;/g, "").trim();
  return withoutEntities.length === 0;
};

type UiPriority = "low" | "medium" | "high";
const PRIORITY_MAP: Record<UiPriority, string> = { high: "HIGH", medium: "MEDIUM", low: "LOW" };

interface TaskDetailContentProps {
  projectId: string;
  taskId: string | null;
  projectKey?: string;
  isCreateMode: boolean;
  onClose: () => void;
  /** "epic" is an Epic's page inside a Sprint project's Epics tab. */
  layout: "modal" | "page" | "epic";
  initialStatusUuid?: string;
  onSuccessSave?: (saved: SavedWorkItem) => void;
  isOpen?: boolean;
  /** Shows the item without any way to change it, e.g. a done item of a completed sprint. */
  readOnly?: boolean;
  /** Why the item is read-only, shown in a banner. */
  readOnlyNote?: string;
}

const TaskDetailContent: React.FC<TaskDetailContentProps> = ({
  projectId,
  taskId,
  projectKey,
  isCreateMode,
  onClose,
  layout,
  initialStatusUuid,
  onSuccessSave,
  isOpen = true,
  readOnly = false,
  readOnlyNote,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toTask, toCreateTask, buildTaskUrl, buildProjectsUrl, buildProjectUrl, buildEpicsUrl, buildEpicUrl } =
    usePmoNavigation();
  const isEpicPage = layout === "epic";
  const authUser = useAppSelector(selectAuthUser);

  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const taskForm = useTaskForm({
    projectId,
    taskId,
    projectKey,
    isCreateMode,
    initialStatusUuid,
    authUser,
    onSuccessSave: onSuccessSave || (() => onClose()),
    isOpen,
  });

  const {
    title, setTitle, titleError, setTitleError, isHydrated, formData, setFormData,
    newTaskTypeUuid, setNewTaskTypeUuid, statusUuid, setStatusUuid,
    assigneeUuid, setAssigneeUuid, reporterUuid, setReporterUuid,
    dueDate, setDueDate, parentUuid, setParentUuid,
    storyPoints, setStoryPoints, isSprintProject, canEstimate,
    plannedSprints, destinationSprintUuid, setDestinationSprintUuid,
    activeTab, setActiveTab, isEditingDescription, setIsEditingDescription,
    isSavingDescription, setIsSavingDescription, previewImage, setPreviewImage,
    isCopied, setIsCopied, childFilters, setChildFilters, markDirty,
    handleEditorChange, handlePriorityChange, handleLabelChange, handleContainerClick,
    handleFilesSelect, handleEditorImageUpload, handleRemoveAttachment,
    handleTask, handleUpdateTask,
    detailManagement, project, defaultData, logs, parentTask, grandParentTask,
    currentCategory, disabledTaskTypeOptions, hideEpicType,
    isSaving, isDeleting, deleteTask,
    isProjectLoading, isDefaultDataLoading,
  } = taskForm;

  const [isChangeParentModalOpen, setIsChangeParentModalOpen] = useState(false);
  const [isSavingParent, setIsSavingParent] = useState(false);

  const handleParentSave = async (newParentUuid: string | null): Promise<boolean> => {
    setIsSavingParent(true);
    return new Promise<boolean>((resolve) => {
      handleUpdateTask(
        {
          workItemParentWorkItemUuid: newParentUuid,
          parentWorkItemUuid: newParentUuid,
        },
        {
          onSuccess: () => {
            setParentUuid(newParentUuid);
            setIsSavingParent(false);
            resolve(true);
          },
          onError: () => {
            setIsSavingParent(false);
            resolve(false);
          },
        }
      );
    });
  };

  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [isSavingConversion, setIsSavingConversion] = useState(false);
  const [isMarkDoneOpen, setIsMarkDoneOpen] = useState(false);
  // A Done status picked in the sidebar while the Epic has open children, saved once confirmed.
  const [pendingDoneStatusUuid, setPendingDoneStatusUuid] = useState<string | null>(null);
  const [isLinkChildrenOpen, setIsLinkChildrenOpen] = useState(false);
  const [hideDoneChildren, setHideDoneChildren] = useState(true);

  const handleConversionSave = async (newWorkTypeUuid: string, parentAction: 'remove' | 'retain'): Promise<boolean> => {
    setIsSavingConversion(true);

    const newWorkType = defaultData?.workTypeDTOList?.find((wt: WorkType) => wt.workTypeUuid === newWorkTypeUuid);
    const newTypeName = newWorkType?.name ||
      (newWorkType?.workTypeCategory
        ? newWorkType.workTypeCategory.charAt(0) + newWorkType.workTypeCategory.slice(1).toLowerCase().replace('_', '-')
        : 'new type');

    return new Promise<boolean>((resolve) => {
      handleUpdateTask(
        {
          workTypeUuid: newWorkTypeUuid,
          ...(parentAction === 'remove' ? { workItemParentWorkItemUuid: null, parentWorkItemUuid: null } : {})
        },
        {
          onSuccess: () => {
            setNewTaskTypeUuid(newWorkTypeUuid);
            if (parentAction === 'remove') {
              setParentUuid(null);
            }
            setIsSavingConversion(false);

            let toastMessage: string;
            if (!parentUuid) {
              toastMessage = `Work item successfully converted to ${newTypeName}.`;
            } else if (parentAction === 'remove') {
              toastMessage = `Work item converted to ${newTypeName}. It is now standalone — parent relationship has been removed.`;
            } else {
              toastMessage = `Work item converted to ${newTypeName}. Parent relationship retained.`;
            }

            toast.success(toastMessage, { duration: 5000 });
            resolve(true);
          },
          onError: () => {
            setIsSavingConversion(false);
            resolve(false);
          },
        }
      );
    });
  };

  const {
    existingTask, isFetchingTask, childItems, linkedItems, attachments,
    fetchNextChildPage, isFetchingMoreChildren, isLoadingInitialChildren,
    childTotalElements, childCurrentPage, childTotalPages, childListVersion, loadingParents,
    totalOpenWorkItemCount, totalInProgressWorkItemCount, totalDoneWorkItemCount,
    handleExpandChild, handleChildAdd, handleUpdateChildItem, handleDeleteChild,
    setLocalLinkedItems,
  } = detailManagement;

  const isEditMode = !isCreateMode;
  const isLoading = (isFetchingTask && !isHydrated) || isProjectLoading || isDefaultDataLoading;

  // Use totalChildWorkItemsCount from the task API response as the authoritative
  // count for the "Child Issues" header badge — this includes all nested subtasks.
  const visibleChildCount = existingTask?.totalChildWorkItemsCount || 0;

  // Breadcrumbs (page layout only)
  const breadcrumbItems = useMemo(() => {
    if (!projectKey) return [];
    const base = [
      { label: "Projects", path: buildProjectsUrl() },
      { label: project?.name ?? "Project", path: buildProjectUrl(projectKey) },
    ];
    const ancestors: { label: string; path: string }[] = [];
    if (grandParentTask) ancestors.push({ label: (grandParentTask as any).workItemKey, path: buildTaskUrl(projectKey, (grandParentTask as any).workItemKey) });
    if (parentTask) ancestors.push({ label: (parentTask as any).workItemKey, path: buildTaskUrl(projectKey, (parentTask as any).workItemKey) });
    return [...base, ...ancestors, { label: isCreateMode ? "Create" : (existingTask?.workItemKey ?? "Edit Task"), path: "#" }];
  }, [grandParentTask, parentTask, project, projectKey, isCreateMode, existingTask, buildProjectsUrl, buildProjectUrl, buildTaskUrl]);

  const handleCopyLink = () => {
    if (!projectKey || !existingTask?.workItemKey) return;
    const url = `${window.location.origin}${buildTaskUrl(projectKey, existingTask.workItemKey)}`;
    navigator.clipboard.writeText(url);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleDeleteTask = () => {
    if (!projectId || !taskId) return;
    deleteTask({ projectUuid: projectId, workItemUuid: taskId }, {
      onSuccess: () => {
        setIsDeleteModalOpen(false);
        const parentKey = parentTask?.workItemKey;
        if (layout === "page" && projectKey && parentKey) {
          toTask(projectKey, parentKey, true);
        } else {
          onClose();
        }
      },
    });
  };


  // Parent task ID for filtering work types
  const parentTaskId = parentUuid || (existingTask as any)?.workItemParentWorkItemUuid || (existingTask as any)?.parentWorkItemUuid;

  // An Epic's status is set by the user; its children never change it.
  const workStatuses: WorkStatus[] = defaultData?.workStatusDTOList ?? [];
  const isEpicDone =
    workStatuses.find((s) => s.workStatusUuid === statusUuid)?.workStatusCategory === WorkStatusCategory.DONE;
  const { data: epicDetail, isLoading: isEpicLoading } = useEpicQuery(
    projectId,
    isEpicPage ? taskId : null,
    hideDoneChildren,
  );
  const epicSummary = epicDetail?.epic;
  // The server can change the Epic's status on its own, e.g. linking a child to a Done
  // Epic reopens it, so the status shown here follows each refetch.
  const serverEpicStatusUuid = epicSummary?.workStatusUuid;
  useEffect(() => {
    if (serverEpicStatusUuid) setStatusUuid(serverEpicStatusUuid);
  }, [serverEpicStatusUuid, setStatusUuid]);
  const markEpicDone = useMarkEpicDoneMutation();
  const reopenEpic = useReopenEpicMutation();
  const isEpicStatusSaving = markEpicDone.isPending || reopenEpic.isPending;

  const changeEpicStatus = async (done: boolean) => {
    if (!taskId) return;
    const mutation = done ? markEpicDone : reopenEpic;
    try {
      const epic = await mutation.mutateAsync({ projectUuid: projectId, epicUuid: taskId });
      if (epic?.workStatusUuid) setStatusUuid(epic.workStatusUuid);
    } catch {
      // The mutation's toast says why.
    }
  };

  // Children are left as they are either way; the warning only makes sure that's meant.
  // Until the Epic's counts load there's no knowing whether to warn, so nothing happens.
  const handleMarkEpicDone = () => {
    if (!epicSummary) return;
    if (epicSummary.openCount > 0) setIsMarkDoneOpen(true);
    else changeEpicStatus(true);
  };

  const closeMarkEpicDone = () => {
    setIsMarkDoneOpen(false);
    setPendingDoneStatusUuid(null);
  };

  const handleConfirmMarkEpicDone = async () => {
    const doneStatusUuid = pendingDoneStatusUuid;
    if (!doneStatusUuid) {
      await changeEpicStatus(true);
      closeMarkEpicDone();
      return;
    }
    handleUpdateTask(
      { workStatusUuid: doneStatusUuid },
      {
        onSuccess: () => { setStatusUuid(doneStatusUuid); closeMarkEpicDone(); },
        onError: closeMarkEpicDone,
      }
    );
  };

  // A Story, Task or Bug's parent is an Epic, picked in the sidebar. A Sub-task's parent
  // stays a Story, Task or Bug, and an Epic has none.
  const showParentEpic =
    isSprintProject && !isEpicPage && ["STORY", "TASK", "BUG"].includes(currentCategory);
  const { data: epicList } = useProjectEpicsQuery(showParentEpic ? projectId : undefined);
  // Adding to a Done Epic reopens it, so what would add to it waits here for the user to confirm.
  const [reopenConfirm, setReopenConfirm] = useState<{ epicTitle?: string; onContinue: () => void } | null>(null);

  // A Task or Bug under a Story has the Story's Epic, not one of its own. The field shows
  // that Epic, and a pick there moves the item out of the Story, so it asks first.
  const parentStory =
    parentUuid &&
    (parentTask as any)?.workItemUuid === parentUuid &&
    (parentTask as any)?.workTypeCategory === WorkTypeCategory.STORY
      ? (parentTask as any)
      : null;
  const storyEpic = !parentStory
    ? null
    : parentStory.epicUuid && parentStory.epicWorkItemKey
      ? { title: parentStory.epicTitle || parentStory.epicWorkItemKey, workItemKey: parentStory.epicWorkItemKey }
      : (grandParentTask as any)?.workTypeCategory === WorkTypeCategory.EPIC
        ? {
            title: (grandParentTask as any).title || (grandParentTask as any).workItemTitle,
            workItemKey: (grandParentTask as any).workItemKey,
          }
        : null;
  // Until the parent is known to be an Epic or a Story, a pick could move the item out of a
  // Story unasked, so the field waits.
  const isParentResolved =
    !parentUuid ||
    (parentTask as any)?.workItemUuid === parentUuid ||
    !!epicList?.epics.some((e) => e.epicUuid === parentUuid);
  // The Epic (or null for none) picked for an item under a Story, waiting for the user to
  // confirm. Kept after closing, so the text doesn't change while the dialog fades out.
  const [leaveStory, setLeaveStory] = useState<{ epic: EpicSummary | null; message: string } | null>(null);
  const [isLeaveStoryOpen, setIsLeaveStoryOpen] = useState(false);

  const applyParentEpic = (epic: EpicSummary | null) => {
    const uuid = epic?.epicUuid ?? null;
    // A new item sends its parent with the create.
    if (isCreateMode) setParentUuid(uuid);
    else handleParentSave(uuid);
  };

  const pickParentEpic = (epic: EpicSummary | null) => {
    if (epic && epic.workStatusCategory === WorkStatusCategory.DONE) {
      setReopenConfirm({ epicTitle: epic.title, onContinue: () => applyParentEpic(epic) });
    } else applyParentEpic(epic);
  };

  const handleParentEpicChange = (epic: EpicSummary | null) => {
    if (!isParentResolved) return;
    if (parentStory) {
      const itemLabel = isCreateMode ? "This work item" : (existingTask as any)?.workItemKey || "This work item";
      const outOfStory = `${itemLabel} will move out of Story ${parentStory.workItemKey}`;
      setLeaveStory({
        epic,
        message: epic ? `${outOfStory} and sit straight under ${epic.title}.` : `${outOfStory} and have no parent.`,
      });
      setIsLeaveStoryOpen(true);
      return;
    }
    if ((epic?.epicUuid ?? null) === (parentUuid || null)) return;
    pickParentEpic(epic);
  };

  // The header's Add / Change Parent can pick a Done Epic too. The picker closes first, so
  // the confirm isn't stacked on it; Cancel leaves the parent as it was.
  const handleParentPicked = async (newParentUuid: string | null, newParent: Task | null): Promise<boolean> => {
    const listedEpic = newParentUuid ? epicList?.epics.find((e) => e.epicUuid === newParentUuid) : undefined;
    const isDoneEpic =
      (newParent?.workTypeCategory === WorkTypeCategory.EPIC &&
        newParent.workStatusCategory === WorkStatusCategory.DONE) ||
      listedEpic?.workStatusCategory === WorkStatusCategory.DONE;
    if (!isDoneEpic) return handleParentSave(newParentUuid);
    setIsChangeParentModalOpen(false);
    setReopenConfirm({
      epicTitle: newParent?.title || newParent?.workItemTitle || listedEpic?.title,
      onContinue: () => handleParentSave(newParentUuid),
    });
    return false;
  };

  // An Epic's own Add child, on a Done Epic.
  const confirmIfEpicDone = (onContinue: () => void) => {
    if (isEpicDone) setReopenConfirm({ epicTitle: title, onContinue });
    else onContinue();
  };

  // Once a Story, Task or Bug is saved as Done, its Epic may have no open children left.
  const promptEpicDone = useEpicDonePrompt();
  const itemEpicUuid =
    existingTask?.epicUuid ||
    ((parentTask as any)?.workTypeCategory === WorkTypeCategory.EPIC ? parentUuid : null);

  const handleStatusChange = (s: WorkStatus) => {
    const isDone = s.workStatusCategory === WorkStatusCategory.DONE;
    // Picking Done here is marking the Epic Done, so it gets the same warning as Mark as Done.
    if (isEpicPage && isDone && !isEpicDone && (epicSummary?.openCount ?? 0) > 0) {
      setPendingDoneStatusUuid(s.workStatusUuid);
      setIsMarkDoneOpen(true);
      return;
    }
    setStatusUuid(s.workStatusUuid);
    handleUpdateTask(
      { workStatusUuid: s.workStatusUuid },
      { onSuccess: () => { if (isDone && showParentEpic) promptEpicDone(projectId, itemEpicUuid); } }
    );
  };

  if (isLoading && isEditMode) return <TaskDetailSkeleton />;

  // In a Sprint project an Epic has its own page under the Epics tab, and only an Epic does.
  if (isEditMode && isHydrated && projectKey && existingTask?.workItemKey) {
    if (layout === "page" && isSprintProject && currentCategory === "EPIC") {
      return <Navigate to={buildEpicUrl(projectKey, existingTask.workItemKey)} replace />;
    }
    if (isEpicPage && currentCategory !== "EPIC") {
      return <Navigate to={buildTaskUrl(projectKey, existingTask.workItemKey)} replace />;
    }
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full bg-primary" onClick={handleContainerClick}>
      <ImagePreviewModal isOpen={!!previewImage} src={previewImage || ""} onClose={() => setPreviewImage(null)} />

      {/* Header — an Epic's page sits under the project's tabs and has its own breadcrumb instead. */}
      {!isEpicPage && (
      <div className="flex justify-between items-center gap-4 flex-wrap py-3 md:pb-4 px-[20px] md:px-[30px] border-b border-primary-subtle shrink-0">
        <div className="flex items-center gap-3">
          {layout === "page" && <MdOutlineZoomOutMap className="text-neutral-medium" />}
          {layout === "page" ? (
            <div className="flex items-center gap-3">
              <Breadcrumbs items={breadcrumbItems} activeLinkProps={{ className: "text-neutral font-semibold" }} />
              <button onClick={handleCopyLink} className="text-neutral-medium hover:text-neutral transition-colors" title="Copy link">
                {isCopied ? <MdCheck size={16} className="text-green-400" /> : <MdContentCopy size={16} />}
              </button>
            </div>
          ) : (
            <Breadcrumbs
              items={[{ label: project?.name || "Project", path: "#" }, { label: existingTask?.workItemKey || "Task", path: "#" }]}
              activeLinkProps={{ className: "text-neutral font-semibold" }}
            />
          )}
        </div>
        <div className="flex items-center gap-3 text-neutral-medium text-2xl">
          {!isEditMode && (
            <LoaderButton className={`!text-sm !py-2 !mt-0 !px-4 md:px-5 ${!title.trim() ? "opacity-50 cursor-not-allowed" : "text-neutral"}`}
              text={layout === "modal" ? "Create Task" : "Create"} isLoading={isSaving}
              disabled={isSaving || !title.trim()} onClick={() => handleTask()} />
          )}
          {isEditMode && layout === "modal" && (
            <Button onClick={() => { if (projectKey && existingTask?.workItemKey) { toTask(projectKey, existingTask.workItemKey); onClose(); } }}
              title="Open in full page" className="hover:text-white"><MdOutlineZoomOutMap /></Button>
          )}
          {isEditMode && layout === "modal" && (
            <Button onClick={handleCopyLink} className={isCopied ? "text-green-400" : "hover:text-white"} title="Copy link">
              {isCopied ? <FiCheck /> : <FiLink />}
            </Button>
          )}
          {isEditMode && !readOnly && currentCategory === "EPIC" && (
            <button
              onClick={() => setIsDeleteModalOpen(true)}
              className="p-2 text-neutral-medium hover:text-red-400 transition-colors flex items-center justify-center rounded-lg hover:bg-red-500/10 outline-none"
              title="Delete"
            >
              <Trash2 size={18} />
            </button>
          )}
          {isEditMode && !readOnly && currentCategory !== "EPIC" && (
            <Menu as="div" className="relative inline-block text-left">
              <MenuButton className="p-2 text-neutral-medium hover:text-white transition-colors flex items-center justify-center rounded-lg hover:bg-white/5 outline-none">
                <MoreVertical size={20} />
              </MenuButton>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems anchor="bottom end" className="w-52 rounded-xl bg-primary-variant border border-primary-subtle shadow-2xl py-1 focus:outline-none z-[9999] mt-2">
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={() => setIsChangeParentModalOpen(true)}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${focus ? "bg-primary-subtle text-white font-medium" : "text-neutral-light"
                          }`}
                      >
                        {parentTaskId ? "Change Parent" : "Add Parent"}
                      </button>
                    )}
                  </MenuItem>
                  {/* A Story's only conversion is to Epic, which a Sprint project's items can't become. */}
                  {currentCategory !== "EPIC" && !(hideEpicType && currentCategory === "STORY") && (
                    <MenuItem>
                      {({ focus }) => (
                        <button
                          onClick={() => setIsConvertModalOpen(true)}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${focus ? "bg-primary-subtle text-white font-medium" : "text-neutral-light"
                            }`}
                        >
                          Change  Work Item Type
                        </button>
                      )}
                    </MenuItem>
                  )}
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={() => setIsDeleteModalOpen(true)}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors text-red-400 ${focus ? "bg-red-500/10 text-red-300 font-medium" : ""
                          }`}
                      >
                        Delete
                      </button>
                    )}
                  </MenuItem>
                </MenuItems>
              </Transition>
            </Menu>
          )}
          <Button onClick={() => layout === "page" ? navigate(-1) : onClose()} className="hover:text-white"><IoCloseSharp /></Button>
        </div>
      </div>
      )}

      {readOnly && (
        <div className="flex items-center gap-2 px-[20px] md:px-[30px] py-2 text-xs text-neutral-medium bg-primary-variant border-b border-primary-subtle shrink-0">
          <Lock size={12} /> {readOnlyNote || "This work item is view only."}
        </div>
      )}

      {/* Body */}
      <div className="flex flex-col md:flex-row flex-1 md:min-h-0 md:overflow-hidden">
        {/* Left column */}
        <div className="flex-1 md:overflow-y-auto md:min-h-0">
          <div className="pt-6 px-[30px] max-991:px-[20px]">
            {isEpicPage && projectKey && (
              <nav className="flex items-center gap-2 pb-3 text-sm" aria-label="Breadcrumb">
                <Link to={buildEpicsUrl(projectKey)} className="text-neutral-medium hover:text-neutral transition-colors">Epics</Link>
                <ChevronRight size={14} className="text-neutral-medium" />
                <span className="text-neutral">{existingTask?.workItemKey}</span>
              </nav>
            )}
            {/* Title row */}
            <div className="flex flex-col gap-1 pb-4 relative">
              <div className="flex flex-wrap items-center gap-1">
                {isEpicPage ? (
                  <span className="flex h-7 w-7 items-center justify-center">{workTypeUIMap.EPIC.icon}</span>
                ) : (
                <TaskTypeDropdown
                  workTypes={(defaultData?.workTypeDTOList ?? []).filter((t: WorkType) => {
                    if (t.workTypeUuid === newTaskTypeUuid) return true;
                    if (t.workTypeCategory === "SUB_TASK" && !parentTaskId) return false;
                    if (t.workTypeCategory === "EPIC" && (parentTaskId || hideEpicType)) return false;
                    if (parentTask) {
                      const allowed = HIERARCHY_RULES[(parentTask as any).workTypeCategory as WorkTypeCategory] || [];
                      if (!allowed.includes(t.workTypeCategory as WorkTypeCategory)) return false;
                    }
                    if (currentCategory === "SUB_TASK") return t.workTypeCategory === "SUB_TASK";
                    return true;
                  })}
                  currentTypeUuid={newTaskTypeUuid!}
                  onTypeChange={(type: WorkType) => {
                    setNewTaskTypeUuid(type.workTypeUuid);
                    markDirty("taskType");
                    if (isEditMode) handleUpdateTask({ workTypeUuid: type.workTypeUuid });
                  }}
                  disabledOptions={disabledTaskTypeOptions}
                  disabled={readOnly}
                  size="md"
                  showDetails
                  footerNote={
                    !hideEpicType || currentCategory === "SUB_TASK" || currentCategory === "EPIC"
                      ? undefined
                      : isEditMode
                        ? "Work items in the Backlog or a sprint can’t become Epics."
                        : "Epics are created from the Epics tab."
                  }
                />
                )}
                <div onClick={() => { if (!readOnly) setIsEditing(true); }}
                  className={`group flex flex-col justify-center ${readOnly ? "" : "cursor-text"} min-h-[28px] ${isEpicPage && !isEditing ? "min-w-0" : "flex-1"} px-2 rounded-md ${titleError ? "border border-red-500 bg-red-500/10" : ""}`}>
                  {isEditing ? (
                    <input autoFocus value={title}
                      onChange={(e) => { setTitle(e.target.value); markDirty("title"); if (e.target.value.trim()) setTitleError(false); }}
                      onBlur={() => {
                        setIsEditing(false);
                        if (!title.trim()) { setTitleError(true); }
                        else if (isEditMode && title.trim() !== (existingTask?.title || (existingTask as any)?.workItemTitle)) {
                          handleUpdateTask({ workItemTitle: title.trim() });
                        }
                      }}
                      maxLength={150} placeholder="Task title is required"
                      className={`w-full font-medium text-lg md:text-xl bg-transparent outline-none border-b ${titleError ? "border-red-500 text-red-500" : "border-primary"}`}
                    />
                  ) : (
                    <h2 className={`font-medium text-lg md:text-xl ${!title ? "text-gray-400" : ""}`}>{title || "Click to add title"}</h2>
                  )}
                </div>
                {isEpicPage && (
                  <>
                    <span className="rounded-md bg-[#818CF822] px-2 py-0.5 text-xs font-medium text-[#A5B4FC]">Epic</span>
                    <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${isEpicDone ? "border-[#10B98144] bg-[#10B98122] text-[#34D399]" : "border-[#3B82F644] bg-[#3B82F622] text-[#60A5FA]"}`}>
                      {isEpicDone ? "Done" : "Open"}
                    </span>
                    {!readOnly && (
                      <div className="ml-auto flex items-center gap-2 pl-3">
                        <button
                          type="button"
                          onClick={isEpicDone ? () => changeEpicStatus(false) : handleMarkEpicDone}
                          disabled={isSaving || isEpicStatusSaving || (!isEpicDone && !epicSummary)}
                          className="flex items-center gap-2 rounded-lg border border-primary-subtle bg-primary-variant px-4 py-2 text-sm font-medium text-neutral hover:bg-primary-subtle transition-colors disabled:opacity-50"
                        >
                          {isEpicDone ? <><RotateCcw size={14} /> Reopen Epic</> : <><Check size={14} /> Mark as Done</>}
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsDeleteModalOpen(true)}
                          className="p-2 text-neutral-medium hover:text-red-400 transition-colors rounded-lg hover:bg-red-500/10"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Description */}
            <CollapsibleSection title="Description">
              <div className="flex flex-col gap-3">
                <div className="rounded-lg transition-all duration-200 border border-primary-subtle bg-primary-variant">
                  {isEditMode && !isEditingDescription ? (
                    <div onDoubleClick={() => { if (!readOnly) setIsEditingDescription(true); }}
                      className={`p-4 min-h-[100px] group relative rounded-lg ${readOnly ? "" : "cursor-text hover:bg-white/5 transition-all"}`}>
                      <div className="prose prose-invert prose-sm max-w-none break-words"
                        dangerouslySetInnerHTML={{
                          __html: !isDescriptionEmpty(formData.description)
                            ? normalizeEmptyParagraphs(formData.description)
                            : `<p class="text-neutral-medium italic">${readOnly ? "No description." : "Double-click to add description..."}</p>`
                        }} />
                      {!readOnly && <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-primary-subtle px-2 py-1 rounded-md text-neutral-medium flex items-center gap-1 text-xs">
                        <Edit2 size={12} />
                        <span>Double-click to edit</span>
                      </div>}
                    </div>
                  ) : (
                    <TipTapEditor key={taskId || "task-content"}
                      initialContent={formData.description} onChange={handleEditorChange}
                      onFilesSelect={handleFilesSelect} onImageUpload={handleEditorImageUpload}
                      mentionUsers={project?.appUserList || []} placeholder="Enter task description..."
                      onImagePreview={setPreviewImage} minHeight={isEditMode ? "150px" : "300px"}
                      autoFocus={isEditingDescription} />
                  )}
                </div>
                {isEditMode && isEditingDescription && (
                  <div className="flex items-center gap-3 justify-end animate-in fade-in slide-in-from-top-2">
                    <button onClick={() => { setFormData(prev => ({ ...prev, description: existingTask?.description || (existingTask as any)?.workItemDescription || "" })); setIsEditingDescription(false); }}
                      disabled={isSaving || isSavingDescription}
                      className="px-4 py-2 text-sm font-medium text-neutral-medium hover:text-white hover:bg-white/5 rounded-lg transition-all">Cancel</button>
                    <button onClick={() => { setIsSavingDescription(true); handleUpdateTask({ workItemDescription: formData.description }); }}
                      disabled={isSaving || isSavingDescription}
                      className="px-6 py-2 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50"
                      style={{ background: "linear-gradient(90deg, #9F54FF 0%, #3B82F6 100%)", boxShadow: "0px 4px 14px rgba(159, 84, 255, 0.25)" }}>
                      {isSavingDescription ? "Saving..." : "Save"}
                    </button>
                  </div>
                )}
              </div>
            </CollapsibleSection>

            {/* Attachments */}
            {attachments.length > 0 && (
              <CollapsibleSection title="Attachments" count={attachments.length} defaultExpanded={true}>
                <AttachmentList attachments={attachments as any} onRemove={handleRemoveAttachment} projectId={projectId} disabled={isSaving || readOnly} />
              </CollapsibleSection>
            )}

            {isEpicPage && isHydrated && projectKey && (
              <>
                {epicSummary && (
                  <>
                    <EpicProgress
                      todo={epicSummary.todoCount}
                      inProgress={epicSummary.inProgressCount}
                      done={epicSummary.doneCount}
                      isEpicDone={isEpicDone}
                    />
                    {!readOnly && (
                      <EpicStatusBanner
                        isEpicDone={isEpicDone}
                        totalCount={epicSummary.totalCount}
                        openCount={epicSummary.openCount}
                        onMarkDone={() => changeEpicStatus(true)}
                        onReopen={() => changeEpicStatus(false)}
                        isSaving={isSaving || isEpicStatusSaving}
                      />
                    )}
                  </>
                )}
                <EpicChildItems
                  items={epicDetail?.children ?? []}
                  totalCount={epicSummary?.totalCount ?? 0}
                  doneCount={epicSummary?.doneCount ?? 0}
                  hideDone={hideDoneChildren}
                  onHideDoneChange={setHideDoneChildren}
                  isLoading={isEpicLoading}
                  members={project?.appUserList ?? []}
                  projectKey={projectKey}
                  readOnly={readOnly}
                  onCreateChild={() =>
                    confirmIfEpicDone(() =>
                      toCreateTask(projectKey, `parent=${taskId}&returnToEpic=${existingTask?.workItemKey ?? ""}`)
                    )
                  }
                  onLinkExisting={() => confirmIfEpicDone(() => setIsLinkChildrenOpen(true))}
                />
              </>
            )}

            {/* Child Items */}
            {!isEpicPage && isEditMode && isHydrated && currentCategory !== "SUB_TASK" && (
              <CollapsibleSection title="Child Issues" count={visibleChildCount}>
                <div className="relative z-40">
                  <ChildItemsList parentTaskTypeCategory={currentCategory as any} childItems={childItems}
                    onChildrenChange={() => { }} availableWorkTypes={defaultData?.workTypeDTOList ?? []}
                    availableWorkStatuses={defaultData?.workStatusDTOList ?? []} availableUsers={project?.appUserList ?? []}
                    onAddChild={handleChildAdd} onUpdateChildItem={handleUpdateChildItem} onDeleteChild={handleDeleteChild}
                    isRemoving={isDeleting} projectId={projectId} projectKey={projectKey}
                    currentTaskUuid={taskId ?? undefined} disabled={isSaving} readOnly={readOnly}
                    childTotalElements={childTotalElements} childCurrentPage={childCurrentPage} childTotalPages={childTotalPages}
                    childListVersion={childListVersion}
                    onLoadMoreChildren={fetchNextChildPage} isLoadingMore={isFetchingMoreChildren}
                    isLoading={isLoadingInitialChildren} onExpandChild={handleExpandChild} loadingParents={loadingParents}
                    activeFilters={childFilters} onApplyFilters={setChildFilters}
                    totalChildWorkItemsCount={existingTask?.totalChildWorkItemsCount}
                    totalOpenWorkItemCount={totalOpenWorkItemCount}
                    totalInProgressWorkItemCount={totalInProgressWorkItemCount}
                    totalDoneWorkItemCount={totalDoneWorkItemCount}
                  />
                </div>
              </CollapsibleSection>
            )}

            {/* Linked Items */}
            {(!isEditMode || isHydrated) && (
              <CollapsibleSection title="Linked Issues" count={linkedItems.length}>
                <div className="relative z-30">
                  <LinkedItemsList linkedItems={linkedItems as any}
                    onLinksChange={(items) => setLocalLinkedItems(items as any)}
                    availableWorkTypes={defaultData?.workTypeDTOList ?? []} disabled={isSaving} readOnly={readOnly}
                    currentTaskUuid={taskId ?? undefined} projectKey={projectKey} projectId={projectId}
                  />
                </div>
              </CollapsibleSection>
            )}
          </div>

          {/* Activity Tabs */}
          {isEditMode && (
            <div className="px-[30px] max-991:px-[20px] pb-10">
              <ActivityTabs activeTab={activeTab} onTabChange={setActiveTab}
                commentCount={existingTask?.workItemCommentDTOList?.length || 0} workLogCount={logs.length} />
              {/* Comments and work log have no read-only mode of their own; inert leaves them readable but untouchable. */}
              <div inert={readOnly}>
              {activeTab === "comments" && (
                <Comments projectUuid={projectId!} workItemUuid={taskId!} projectKey={projectKey!}
                  workItemKey={existingTask?.workItemKey || ""}
                  existingComments={existingTask?.workItemCommentDTOList || []}
                  mentionUsers={project?.appUserList || []} />
              )}
              {activeTab === "history" && <TaskHistory projectUuid={projectId!} workItemUuid={taskId!} />}
              {activeTab === "work-log" && taskId && (
                <WorkLog projectId={projectId!} taskId={taskId} projectKey={projectKey!} workItemKey={existingTask?.workItemKey || ""} highlightUserUuid={location.state?.highlightUserUuid} />
              )}
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="w-full md:w-80 border-t md:border-t-0 border-l border-primary-subtle px-6 pb-6 md:overflow-y-auto md:min-h-0">
          <div className="space-y-6 pt-6">
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2">Status</label>
              {/* On an Epic's page, waits for its counts so a Done pick knows whether to warn. */}
              <StatusDropdown workStatuses={defaultData?.workStatusDTOList ?? []} currentStatusUuid={statusUuid!}
                disabled={readOnly || (isEpicPage && !epicSummary)}
                onStatusChange={handleStatusChange}
                size="md" className="w-full bg-primary-variant h-10 rounded-lg border border-primary-subtle pl-2 flex items-center" />
            </div>
            {showParentEpic && (
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2">
                  Parent Epic <span className="font-normal">(optional)</span>
                </label>
                {/* '' checks nothing: an item under a Story has no Epic of its own, and isn't parentless. */}
                <EpicPickerDropdown epics={epicList?.epics ?? []}
                  value={parentStory || !isParentResolved ? "" : parentUuid}
                  viaStory={parentStory ? { storyKey: parentStory.workItemKey, epic: storyEpic } : undefined}
                  onChange={handleParentEpicChange}
                  disabled={readOnly || isSaving || isSavingParent || !isParentResolved}
                  className="w-full bg-primary-variant h-10 rounded-lg border border-primary-subtle pl-3" />
              </div>
            )}
            {/* A new item goes to the Backlog or a planned sprint. Epics are never in one, and a
                Sub-task goes with its parent. */}
            {isCreateMode && isSprintProject && currentCategory !== "EPIC" && currentCategory !== "SUB_TASK" && (
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2">Sprint</label>
                <SprintDestinationDropdown sprints={plannedSprints} value={destinationSprintUuid}
                  onChange={setDestinationSprintUuid} disabled={isSaving}
                  className="w-full bg-primary-variant h-10 rounded-lg border border-primary-subtle pl-3" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2">Assignee</label>
              <UserDropdown users={project?.appUserList ?? []} currentUserUuid={assigneeUuid}
                onUserChange={(u: ProjectUser | null) => { const id = u?.appUserUuid || null; setAssigneeUuid(id); handleUpdateTask({ workItemAssigneeAppUserUuid: id }); }}
                placeholder="Unassigned" showEmail size="md" showName disabled={readOnly}
                className="w-full bg-primary-variant h-10 rounded-lg border border-primary-subtle px-3 flex items-center" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2">Reporter</label>
              <UserDropdown users={project?.appUserList ?? []} currentUserUuid={reporterUuid}
                onUserChange={(u: ProjectUser | null) => { const id = u?.appUserUuid || null; setReporterUuid(id); handleUpdateTask({ workItemReporterAppUserUuid: id }); }}
                placeholder="Unassigned" showEmail size="md" showName allowUnassigned={false} disabled={readOnly}
                className="w-full bg-primary-variant h-10 rounded-lg border border-primary-subtle px-3 flex items-center" />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2">Priority</label>
              <PriorityDropdown currentPriority={formData.priority} disabled={readOnly}
                onPriorityChange={(priority: any) => { handlePriorityChange(priority); handleUpdateTask({ workPriority: PRIORITY_MAP[priority as UiPriority] }); }}
                showLabel size="md" className="w-full bg-primary-variant h-10 rounded-lg border border-primary-subtle pl-3 flex items-center" />
            </div>
            {/* Only stories in a Sprint project can hold points, so never an Epic. */}
            {canEstimate && (
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2">Story Points</label>
                <StoryPointsInput
                  value={storyPoints}
                  disabled={isSaving || readOnly}
                  onCommit={(points) => {
                    const previous = storyPoints;
                    setStoryPoints(points);
                    // A new item sends its points with the create instead.
                    if (isEditMode) handleUpdateTask({ storyPoints: points }, { onError: () => setStoryPoints(previous) });
                  }}
                />
              </div>
            )}
            {isEditMode && taskId && (
              <div inert={readOnly}>
                <TimeTrackerSidebar projectId={projectId!} taskId={taskId} />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2">Label</label>
              <LabelPicker projectId={projectId!} selectedLabelUuids={formData.labels} disabled={readOnly}
                onLabelsChange={(labels: string[]) => { handleLabelChange(labels); handleUpdateTask({ projectLabelUuidList: labels }); }}
                className="w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2">Due Date</label>
              <CustomCalendar value={dueDate ? new Date(dueDate) : ""} disabled={readOnly}
                onChange={(date: Date) => { const f = formatDateKey(date); setDueDate(f); handleUpdateTask({ workItemDueDate: f }); }}
                placeholder="Select due date" minDate={new Date()}
                className="bg-primary-variant text-sm border-primary-subtle h-10 rounded-lg shadow-none" dropdownPosition="top" />
            </div>
            {isSprintProject && currentCategory === "EPIC" && (
              <p className="rounded-lg border border-primary-subtle px-3 py-2.5 text-xs leading-relaxed text-neutral-medium">
                Epics aren’t planned in sprints and have no Story Points. Plan their child work items instead.
              </p>
            )}
            {isEditMode && existingTask && (
              <div className="pt-6 mt-6 border-t border-primary-subtle flex flex-col gap-4 text-sm text-neutral-medium">
                {existingTask.createdDate && (
                  <div className="flex items-center gap-3"><MdAccessTime className="w-4 h-4" />
                    <span>Created {new Date(existingTask.createdDate).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</span>
                  </div>
                )}
                {existingTask.lastModifiedAt && (
                  <div className="flex items-center gap-3"><MdHistory className="w-4 h-4" />
                    <span>Updated {(() => {
                      const m = new Date(existingTask.lastModifiedAt);
                      const h = Math.round((Date.now() - m.getTime()) / 3600000);
                      if (h < 1) return "just now";
                      if (h < 24) return `${h} hour${h > 1 ? "s" : ""} ago`;
                      const d = Math.round(h / 24);
                      if (d < 30) return `${d} day${d > 1 ? "s" : ""} ago`;
                      return m.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
                    })()}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <EnhancedDeleteModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteTask} isDeleting={isDeleting} entityType="WORK_ITEM"
        entityKey={existingTask?.workItemKey || ""} workTypeCategory={existingTask?.workTypeCategory}
        counts={{ subtasks: existingTask?.totalChildWorkItemsCount ?? 0, comments: existingTask?.totalCommentsCount ?? 0, attachments: existingTask?.totalMediaCount ?? 0 }} />

      {isEditMode && (
        <ChangeParentModal
          isOpen={isChangeParentModalOpen}
          onClose={() => setIsChangeParentModalOpen(false)}
          projectId={projectId}
          workItemUuid={taskId || ""}
          currentWorkItemKey={existingTask?.workItemKey || ""}
          currentWorkTypeCategory={currentCategory as WorkTypeCategory}
          currentParentUuid={parentUuid}
          currentParentTitle={parentTask ? `${(parentTask as any).workItemKey}: ${(parentTask as any).title || (parentTask as any).workItemTitle}` : undefined}
          availableWorkTypes={defaultData?.workTypeDTOList ?? []}
          onSave={handleParentPicked}
          isSaving={isSavingParent}
        />
      )}

      {isEpicPage && taskId && (
        <>
          <MarkEpicDoneModal
            isOpen={isMarkDoneOpen}
            onClose={closeMarkEpicDone}
            openChildCount={epicSummary?.openCount ?? 0}
            isSaving={markEpicDone.isPending || isSaving}
            onConfirm={handleConfirmMarkEpicDone}
          />
          <LinkEpicChildrenModal
            isOpen={isLinkChildrenOpen}
            onClose={() => setIsLinkChildrenOpen(false)}
            projectUuid={projectId}
            epicUuid={taskId}
          />
        </>
      )}

      <ConfirmDeleteModal
        isOpen={!!reopenConfirm}
        onClose={() => setReopenConfirm(null)}
        onConfirm={() => {
          reopenConfirm?.onContinue();
          setReopenConfirm(null);
        }}
        title="Adding a work item will reopen this Epic"
        message={`${reopenConfirm?.epicTitle || "This Epic"} is Done. Its existing child work items don’t change.`}
        confirmLabel="Continue"
        variant="primary"
      />

      {/* A Done Epic then asks about reopening it, as any other pick does. */}
      <ConfirmDeleteModal
        isOpen={isLeaveStoryOpen}
        onClose={() => setIsLeaveStoryOpen(false)}
        onConfirm={() => {
          setIsLeaveStoryOpen(false);
          if (leaveStory) pickParentEpic(leaveStory.epic);
        }}
        title="Move out of the Story?"
        message={leaveStory?.message ?? ""}
        confirmLabel="Continue"
        variant="primary"
      />

      {isEditMode && (
        <ConvertWorkItemModal
          isOpen={isConvertModalOpen}
          onClose={() => setIsConvertModalOpen(false)}
          workItemKey={existingTask?.workItemKey || ""}
          workItemTitle={existingTask?.title || (existingTask as any)?.workItemTitle || ""}
          currentWorkTypeCategory={currentCategory as WorkTypeCategory}
          currentParentUuid={parentUuid}
          currentParentCategory={(parentTask as any)?.workTypeCategory as WorkTypeCategory}
          availableWorkTypes={(defaultData?.workTypeDTOList ?? []).filter(
            (t: WorkType) => !(hideEpicType && t.workTypeCategory === "EPIC")
          )}
          onSave={handleConversionSave}
          isSaving={isSavingConversion}
          hasChildren={visibleChildCount > 0}
        />
      )}
    </div>
  );
};

export default TaskDetailContent;
